import * as React from 'react';
import DialogContent from '@mui/material/DialogContent';
import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { GlobalContext } from '../../Context/GlobalContext';
import { CircularProgress, MenuItem, Dialog, DialogTitle, FormControl, InputLabel, Select, DialogActions, Button} from '@mui/material';
import useAxios from '../../Hooks/useAxios';
import { useState } from 'react';



/**
 * A reusable Paper component for the draggable dialog. This component is used to wrap the dialog content
 * and enable dragging functionality.
 * 
 * @component
 * @param {Object} props - The component props.
 * @param {React.RefObject} props.nodeRef - A ref to the Paper component.
 * @returns {JSX.Element} A draggable Paper component.
 */
function PaperComponent(props) {
  const nodeRef = React.useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper
        {...props}
        ref={nodeRef}
        className="formPaperSelectTask"
        sx={{
          borderRadius: "30px"
        }}/>
    </Draggable>
  );
}

/**
 * A popup dialog component for selecting a task type. It displays a list of process types fetched from an API
 * and allows the user to select a task type to create a new task.
 * 
 * @component
 * @returns {JSX.Element} The rendered task selection popup dialog component.
 */
function SelectTaskPopup() {
  const { selectTaskOpen, setSelectTaskOpen, setTaskType, setNewTaskOpen } = React.useContext(GlobalContext);

  const { data, isLoading, error } = useAxios("processTypes");
  const [processes, setProcesses] = React.useState([]);

   const [selectedProcess, setSelectedProcess] = useState(null);
   const [processPreview, setProcessPreview] = useState("");
   const [open, setOpen] = useState(false);

  // Styles for different process types
  const processStyles = {
    1: { background: "#96cd39", '&:hover': {backgroundColor: '#e0ffcd', border: '1px solid green', color: "green"}},
    4: { background: "#ff5b44", '&:hover': {backgroundColor: '#ffcab0', border: '1px solid red', color: "red"}}
  };

  /**
   * Fetches process types from the API and updates the state with the fetched data.
   */
  React.useEffect(() => {
    if (!isLoading) {
      if (error) {
        console.error(error);
      } else {
        setProcesses([...data.map(item => {return {...item, disabled: false, styles: processStyles[item.id]}})]);
      }
    }
  }, [isLoading]);

  /**
   * Handles the selection of a task type. Updates the global context with the selected task type
   * and opens the new task form.
   * 
   * @param {Object} item - The selected process type.
   */
  const onClickOptionHandler = (item) => {
    setTaskType({id: item.id, name: item.name});
    setSelectTaskOpen(false);
    setNewTaskOpen(true);
  };

  /**
   * Handles closing the dialog.
   */
  const handleClose = () => {
    setSelectTaskOpen(false);
  };
  
  /**
   * Renders the list of process types as selectable menu items.
   * 
   * @returns {Array} An array of `MenuItem` components representing the process types.
   */
  const displayButtons = () => {
    return [...processes.map((item) =>{
      return <MenuItem
        onClick={onClickOptionHandler.bind(null, item)}
        sx={{color: "white", fontSize:"25px", borderRadius: "33px", mb: "5px", justifyContent: "center", ...item.styles}}
        disabled={item.disabled}
      >
        {item.name}
      </MenuItem>
    })]
  }

  const displayProcessMenu = () => {
       return (
           <>
             <FormControl fullWidth sx={{ mt: 2 }}>
               <InputLabel>Process</InputLabel>

               <Select
                 value={selectedProcess?.name || ""}
                 label="Process"
                 onChange={(event) => {
                   const process = processes.find(
                     (item) => item.name === event.target.value
                   );

                   setSelectedProcess(process);
                 }}
               >
                 {processes.map((item) => (
                   <MenuItem
                     key={item.name}
                     value={item.name}
                     disabled={item.disabled}
                   >
                     {item.name}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>

             <Button
               sx={{ mt: 2 }}
               variant="contained"
               onClick={() => {
                 if (selectedProcess) {
                   onClickOptionHandler(selectedProcess);
                 }
                 handleClose();
               }}
             >
               Accept
             </Button>
           </>
         );
  };


  return (
    <> 
    <Dialog
      open={selectTaskOpen}
      onClose={handleClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"      
    >
    <DialogContent
        sx={{
        maxHeight: '10vh',
        overflowY: 'auto'
        }}>
      {isLoading? <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</> : displayProcessMenu()}
      </DialogContent>
    </Dialog>
  </>
  );
}

export default SelectTaskPopup;
